/**
 * Base58 and Base58Check decoding for address validation.
 *
 * Tron and Solana addresses are Base58-encoded. This module decodes them so the
 * send form can reject a malformed address before the gateway ever sees it. It
 * holds no key material and performs no signing: decoding an address is the only
 * thing it does, and the bytes it produces are compared against a checksum or a
 * length, never used to derive or sign anything.
 *
 * The Base58 alphabet is the Bitcoin alphabet, which omits the visually
 * ambiguous characters 0, O, I, and l.
 */

import { sha256d } from './sha256';

/** The Bitcoin Base58 alphabet, used by both Tron and Solana. */
const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

/**
 * Reverse lookup from a Base58 character to its value, built once. A character
 * outside the alphabet maps to undefined, which the decoder treats as invalid.
 */
const INDEX: Map<string, number> = (() => {
  const map = new Map<string, number>();
  for (let i = 0; i < ALPHABET.length; i += 1) map.set(ALPHABET[i], i);
  return map;
})();

/**
 * Decode a Base58 string to its raw bytes. Leading "1" characters encode leading
 * zero bytes, per the Base58 convention. Returns null when the input contains a
 * character outside the alphabet, so a caller can fail closed on bad input
 * rather than throw.
 *
 * @param input The Base58 string to decode.
 * @returns The decoded bytes, or null when input is invalid.
 */
export function base58Decode(input: string): Uint8Array | null {
  if (typeof input !== 'string' || input.length === 0) return null;

  // Accumulate the big-endian integer the Base58 string represents, as an array
  // of significant bytes built by repeated multiply-by-58 and add. This is the
  // standard schoolbook base conversion; it is not performance critical for an
  // address. The accumulator starts empty so an all-"1" input (the integer zero)
  // produces no significant bytes, and the leading-zero handling below supplies
  // the zero bytes those leading "1" characters encode.
  const bytes: number[] = [];
  for (const ch of input) {
    const value = INDEX.get(ch);
    if (value === undefined) return null;

    let carry = value;
    for (let j = 0; j < bytes.length; j += 1) {
      carry += bytes[j] * 58;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }

  // Each leading "1" in the input is one leading zero byte in the output.
  let leadingZeros = 0;
  for (const ch of input) {
    if (ch === '1') leadingZeros += 1;
    else break;
  }

  // The byte array was built little-endian; reverse it and prepend the zeros.
  const out = new Uint8Array(leadingZeros + bytes.length);
  for (let i = 0; i < bytes.length; i += 1) {
    out[leadingZeros + i] = bytes[bytes.length - 1 - i];
  }
  return out;
}

/**
 * Decode a Base58Check string and verify its trailing 4-byte checksum. The
 * checksum is the first four bytes of the double SHA-256 of the payload. Returns
 * the payload without the checksum when it verifies, or null when the input is
 * not valid Base58, is too short to carry a checksum, or fails the checksum.
 *
 * @param input The Base58Check string.
 * @returns The verified payload, or null when invalid.
 */
export function base58CheckDecode(input: string): Uint8Array | null {
  const raw = base58Decode(input);
  if (!raw || raw.length < 5) return null;

  const payload = raw.subarray(0, raw.length - 4);
  const checksum = raw.subarray(raw.length - 4);
  const expected = sha256d(payload).subarray(0, 4);

  // Constant work over four bytes; an address checksum is not secret, so a plain
  // comparison is fine here.
  for (let i = 0; i < 4; i += 1) {
    if (checksum[i] !== expected[i]) return null;
  }
  return payload;
}
